/**
 * Zahnheld configurator controller.
 *
 * Drives sections/zh-main-product.liquid: one base device plus editor-configured accessory groups, optional presets,
 * a global colour that every colour-capable position follows, and a single multi-line add to cart.
 *
 * Three invariants this file is built around:
 *
 * 1. It never computes a discount. Selling-plan prices come from `allocations[planId].price`, which Liquid took from
 *    Shopify's own evaluation of the pricing policy. Compare-at prices come from the catalogue. Sums are composed from
 *    raw cents and formatted through the shop money format, so the displayed total cannot disagree with the cart.
 * 2. Colour is one value, not one per position. A product without a colour option ignores it, so a mixed-colour state
 *    is not representable rather than merely discouraged.
 * 3. Availability is evaluated per resolved colour. Bürstenkopf Basic Mini is purchasable in Schwarz and sold out in
 *    Weiß, so a card that is fine in one colour must go disabled in the other instead of silently adding nothing.
 *
 * The pure helpers are exported on `window.__zhKonf` so they can be unit tested without a DOM.
 */
(function () {
  'use strict';

  /* Seal's marker on the recommended subscription plan, lower-cased. Same string as in zh-konf-abo-step.liquid. */
  var POPULAR_PLAN_MARKER = 'am beliebtesten';

  /* ------------------------------------------------------------------------------------------------------------------
   * Money
   * ---------------------------------------------------------------------------------------------------------------- */

  function formatDelimited(cents, precision, thousands, decimal) {
    var fixed = (cents / 100).toFixed(precision);
    var parts = fixed.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d{3})+$)/g, '$1' + thousands);
    return precision > 0 ? whole + decimal + parts[1] : whole;
  }

  /**
   * Formats cents using the shop's money format string. Mirrors Shopify's own placeholder set so a shop configured
   * with, say, `amount_with_comma_separator` renders the same string the server would.
   */
  function createMoneyFormatter(moneyFormat) {
    return function formatMoney(cents) {
      var format = moneyFormat || '';
      var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
      var key = match ? match[1] : 'amount';
      var value;

      switch (key) {
        case 'amount_no_decimals':
          value = formatDelimited(cents, 0, ',', '.');
          break;
        case 'amount_with_comma_separator':
          value = formatDelimited(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatDelimited(cents, 0, '.', ',');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatDelimited(cents, 2, "'", '.');
          break;
        case 'amount_with_space_separator':
          value = formatDelimited(cents, 2, ' ', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = formatDelimited(cents, 0, ' ', ',');
          break;
        case 'amount_with_period_and_space_separator':
          value = formatDelimited(cents, 2, ' ', '.');
          break;
        default:
          value = formatDelimited(cents, 2, ',', '.');
      }

      return match ? format.replace(match[0], value) : value;
    };
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * Variant resolution
   * ---------------------------------------------------------------------------------------------------------------- */

  /**
   * Picks the variant of `handle` that matches the requested colour and pack size.
   *
   * Fallback order is deliberate: an exact (colour, count) match wins; failing that the colour is kept and the count
   * dropped, because the customer explicitly chose the colour and only inherited the pack size; failing that any
   * purchasable variant; finally the first variant, so callers always get something to price.
   *
   * `extraOptions` matches base-device options that are neither colour nor pack size, keyed by option name.
   */
  function resolveVariant(data, handle, color, count, extraOptions) {
    var product = data.products && data.products[handle];
    if (!product || !product.variants || !product.variants.length) return null;

    var wantColor = product.hasColor ? color || '' : '';
    var wantCount = product.hasCount ? count || '' : '';
    var names = product.optionNames || [];

    function extrasMatch(variant) {
      if (!extraOptions) return true;
      for (var name in extraOptions) {
        if (!Object.prototype.hasOwnProperty.call(extraOptions, name)) continue;
        var index = names.indexOf(name);
        if (index === -1) continue;
        if ((variant.options || [])[index] !== extraOptions[name]) return false;
      }
      return true;
    }

    function find(useCount) {
      for (var i = 0; i < product.variants.length; i += 1) {
        var variant = product.variants[i];
        if (wantColor && variant.color !== wantColor) continue;
        if (useCount && wantCount && variant.count !== wantCount) continue;
        if (!extrasMatch(variant)) continue;
        return variant;
      }
      return null;
    }

    var match = find(true) || find(false);
    if (match) return match;

    for (var j = 0; j < product.variants.length; j += 1) {
      if (product.variants[j].available) return product.variants[j];
    }
    return product.variants[0];
  }

  /** Lowest purchasable plan price for a variant, or its one-time price when it has no allocations. */
  function cheapestPlanPrice(variant) {
    if (!variant) return 0;
    var lowest = variant.price;
    var allocations = variant.allocations || {};
    for (var id in allocations) {
      if (!Object.prototype.hasOwnProperty.call(allocations, id)) continue;
      if (allocations[id].price < lowest) lowest = allocations[id].price;
    }
    return lowest;
  }

  /** Price of one unit, honouring the chosen selling plan. Never recomputes the plan discount. */
  function unitPriceFor(variant, planId) {
    if (!variant) return 0;
    if (planId && variant.allocations && variant.allocations[planId]) {
      return variant.allocations[planId].price;
    }
    return variant.price;
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * Selection model
   * ---------------------------------------------------------------------------------------------------------------- */

  /** The group that offers `handle`, either as a regular option or as its swap target. Null when no group does. */
  function ownerGroupFor(data, handle) {
    var owner = null;
    (data.groups || []).forEach(function (group) {
      if (owner) return;
      if ((group.products || []).indexOf(handle) !== -1) owner = group;
      if (!owner && group.swapInHandle && group.swapInHandle === handle) owner = group;
    });
    return owner;
  }

  /**
   * The selection a step carries when no set has spoken for it.
   *
   * Nothing, because that is what an unanswered optional step is and what every multi-choice step shows on a fresh
   * page. A required single-choice step is the exception: it renders no opt-out row, so empty is a state it cannot
   * express, and there the recommendation stands in, exactly as Liquid paints it.
   */
  function groupFallback(group) {
    var handles = [];

    if (group.required && group.selection !== 'multi') {
      var recommended = group.defaultHandle || (group.products || [])[0] || '';
      if (recommended) handles.push(recommended);
    }

    return { handles: handles, count: group.defaultCount || '', qty: group.qty || 1 };
  }

  /**
   * Splits a preset's positions into the steps that can express them and the rest.
   *
   * A position whose product is offered by a step becomes that step's selection, so the customer can still change it.
   * A position that is the base device only adjusts the base quantity. Everything left over becomes a fixed extra: it
   * ships with the set but the configurator offers no control for it. The second device used to be that case; it has a
   * step of its own now, so this is the fallback for a template that does not carry one.
   *
   * The result is a statement about EVERY step, not only the ones the set names. While it was partial, two things went
   * wrong, and both surfaced the same way: a set cost something else in the cart than its own card advertises, because
   * `presetFloorPrice` sums the set's positions and nothing besides.
   *
   *   - A step the set omits kept whatever the previous set or a manual tick had left in it. With a second device on
   *     offer that is a whole device surviving into a set that does not price it.
   *   - A multi-choice step named several times by one set kept only its last position. Premium lists three Pflege
   *     articles, all owned by one step, so two of them were dropped without a word.
   */
  function applyPreset(data, preset) {
    var result = { groups: {}, extras: [], baseQty: 1 };
    /* Which steps the set has spoken for, so a second position accumulates instead of replacing the first. */
    var named = {};

    (data.groups || []).forEach(function (group) {
      result.groups[group.key] = groupFallback(group);
    });

    if (!preset || !preset.items) return result;

    preset.items.forEach(function (item) {
      if (item.handle === data.baseHandle) {
        result.baseQty = item.qty || 1;
        return;
      }

      var owner = ownerGroupFor(data, item.handle);

      if (!owner) {
        result.extras.push({ handle: item.handle, qty: item.qty || 1, count: item.count || '' });
        return;
      }

      var entry = result.groups[owner.key];

      if (named[owner.key] && owner.selection === 'multi') {
        if (entry.handles.indexOf(item.handle) === -1) entry.handles.push(item.handle);
        return;
      }

      /* First position of this step, or a single-choice step named twice: the position replaces what was there. */
      named[owner.key] = true;
      entry.handles = [item.handle];
      entry.count = item.count || '';
      entry.qty = item.qty || 1;
    });

    return result;
  }

  /** Every handle the current state puts in the cart. Drives only_if_handle and the swap rule. */
  function activeHandles(data, state) {
    var handles = [data.baseHandle];
    state.extras.forEach(function (extra) {
      handles.push(extra.handle);
    });
    (data.groups || []).forEach(function (group) {
      var selection = state.groups[group.key];
      if (!selection) return;
      (selection.handles || []).forEach(function (handle) {
        handles.push(handle);
      });
    });
    return handles;
  }

  /**
   * The handles a group should carry once its swap rule has been applied.
   *
   * A group can declare "if the customer picks X, replace Y with Z". The incoming product Z
   * is rendered up front inside a container the controller hides, so it exists in the DOM
   * whether the rule holds or not. That is what made two bugs possible, and both were paid
   * for in a real order:
   *
   *   - Z survived the rule going inactive. Its card is inside the hidden slot, so the
   *     selection was invisible AND unremovable, and it was still charged.
   *   - The old code assigned `[Z]` over the whole list. Pflege is multi-select, so
   *     activating the rule silently dropped the Serum and the Pen.
   *
   * Hence: substitute Y with Z in place when the rule holds, and substitute BACK when it
   * stops holding. Both directions keep the position, because that position is what the
   * summary renders and what the preview reads.
   *
   * Never ADD Z to a group that never had Y, or picking a brush head would bill a toothpaste
   * nobody asked for. And never add Y back if Z is not there, so a customer who unchecked
   * the substitute stays opted out across the flip.
   *
   * Pure and returns a new array, so syncCards can compare before and after to decide
   * whether state needs writing at all.
   */
  function applySwap(handles, group, swapActive) {
    var next = (handles || []).slice();
    if (!group || !group.swapInHandle) return next;

    if (!swapActive) {
      /*
        Reverse the substitution rather than just dropping Z. Z can only have got into the
        list through the rule, because its card lives inside the slot the controller hides
        while the rule is off, so the customer cannot have picked it. Dropping it alone left
        the step with nothing selected, which is not what a substitution reverses to.

        If the customer unchecked Z while the rule held, there is nothing to reverse and
        nothing gets added: opting out has to survive the flip.
      */
      var atIn = next.indexOf(group.swapInHandle);
      if (atIn === -1) return next;

      next.splice(atIn, 1);
      if (group.swapOutHandle && next.indexOf(group.swapOutHandle) === -1) {
        next.splice(atIn, 0, group.swapOutHandle);
      }
      return next;
    }

    var at = next.indexOf(group.swapOutHandle);
    if (at === -1) return next;

    next.splice(at, 1);
    /* Drop any stray copy first, so the flip can never leave both halves selected. */
    next = next.filter(function (handle) {
      return handle !== group.swapInHandle;
    });
    next.splice(at, 0, group.swapInHandle);
    return next;
  }

  /** A group is shown unless it is gated on a handle the current set does not contain. */
  function groupVisible(data, state, group) {
    if (!group.onlyIfHandle) return true;
    return activeHandles(data, state).indexOf(group.onlyIfHandle) !== -1;
  }

  /**
   * The product the preview frame should show on behalf of `state.mediaGroup`, the step that currently owns it. Empty
   * when no step owns the frame, or when the owning step is opted out of, which sends the caller back to the set photo
   * rather than leaving a stale accessory in view.
   *
   * For a multiple-choice step the LAST handle wins: the change handler rebuilds the list by dropping the touched
   * handle and pushing it back on, so the end of the list is the tick that just happened. Showing that one rather than
   * the first is what makes the frame answer the tap.
   */
  function previewHandle(state) {
    if (!state.mediaGroup) return '';
    var selection = state.groups[state.mediaGroup];
    if (!selection || !selection.handles || !selection.handles.length) return '';
    return selection.handles[selection.handles.length - 1];
  }

  /**
   * Builds the /cart/add.js payload. Order matters for the cart drawer: the device first, then its accessories in step
   * order, then the fixed extras of the set.
   */
  function buildItems(data, state, configId) {
    var items = [];

    function push(handle, qty, count, planId) {
      var variant = resolveVariant(data, handle, state.color, count, state.baseOptions);
      if (!variant || !variant.available || qty < 1) return;
      var item = { id: variant.id, quantity: qty };
      if (planId) item.selling_plan = parseInt(planId, 10);
      item.properties = { _zh_konf: state.presetKey || 'custom', _zh_konf_id: configId };
      items.push(item);
    }

    push(data.baseHandle, state.baseQty || 1, '', '');

    (data.groups || []).forEach(function (group) {
      if (!groupVisible(data, state, group)) return;
      var selection = state.groups[group.key];
      if (!selection) return;
      (selection.handles || []).forEach(function (handle) {
        push(handle, selection.qty || 1, selection.count || '', selection.planId || '');
      });
    });

    state.extras.forEach(function (extra) {
      push(extra.handle, extra.qty || 1, extra.count || '', '');
    });

    return items;
  }

  /** Sum in cents of everything selected, plus the comparable sum where a compare-at price exists. */
  function computeTotals(data, state) {
    /*
      Two passes, because the second-device rate is a property of the WHOLE configuration and not of one position:
      Shopify discounts the cheapest device, and which one that is only shows once every device has been priced.
    */
    var positions = [];

    function collect(handle, qty, count, planId) {
      var variant = resolveVariant(data, handle, state.color, count, state.baseOptions);
      if (!variant) return;
      /* Drei Ebenen, und sie duerfen nicht durcheinandergeraten:
           variant.price / compareAt  -> der Katalog, und damit die Referenz unten
           unitPriceFor(...)          -> was der Abo-Plan daraus macht
           discountedUnit(...)        -> was der Konfigurator-Rabatt daraus macht
         Der Rabatt sitzt auf der untersten Ebene. Die Referenz bleibt der Katalog, sonst stuende der Rabatt in beiden
         Zahlen und die Ersparnis verschwaende in der Differenz. */
      positions.push({
        handle: handle,
        qty: qty,
        planId: planId,
        unit: unitPriceFor(variant, planId),
        /*
          The comparable reference is the highest honest one: a catalogue compare_at when there is one, otherwise the
          one-time price. That makes the abo difference count as a saving, because on this order the customer really
          does pay the allocation price instead of the one-time price. Both come from Shopify, nothing is invented here.
        */
        reference: variant.compareAt > variant.price ? variant.compareAt : variant.price
      });
    }

    collect(data.baseHandle, state.baseQty || 1, '', '');

    (data.groups || []).forEach(function (group) {
      if (!groupVisible(data, state, group)) return;
      var selection = state.groups[group.key];
      if (!selection) return;
      (selection.handles || []).forEach(function (handle) {
        collect(handle, selection.qty || 1, selection.count || '', selection.planId || '');
      });
    });

    state.extras.forEach(function (extra) {
      collect(extra.handle, extra.qty || 1, extra.count || '', '');
    });

    var discountedDevice = discountedDeviceFor(data, positions);
    var total = 0;
    var compare = 0;
    var hasCompare = false;

    positions.forEach(function (position) {
      var percent = discountPercentFor(data, position.handle, position.planId, discountedDevice);
      total += positionTotal(position, percent, position.handle === discountedDevice);
      compare += position.reference * position.qty;
      if (position.reference > discountedUnit(position.unit, percent)) hasCompare = true;
    });

    return { total: total, compare: hasCompare ? compare : 0 };
  }

  /**
   * Cheapest unit price of `handle` the customer can still reach, or null when the product is unknown.
   *
   * The single definition of "ab" on the client side, mirrored by snippets/zh-konf-cheapest.liquid. What counts as
   * reachable:
   *
   *   - Colour: always free. The customer picks it globally, so every colour is a candidate and the result is
   *     colour-independent by construction rather than by accident of equal prices.
   *   - Pack size: a candidate unless the caller pins it, which callers do exactly when the step refuses to let the
   *     customer change it.
   *   - Sold out: excluded, because a price nobody can buy is not a floor. When nothing is purchasable the cheapest
   *     variant is used anyway, so callers get a number instead of collapsing to zero.
   *   - Subscription: only when the caller says so, meaning only where a group with allow_abo actually offers a
   *     subscription step. Assuming it elsewhere would advertise a saving the cart never grants, which is the exact
   *     failure mode the ZH-UP upsells already had.
   */
  /**
   * Whether `needle` is a whole entry of a comma separated handle list.
   *
   * Commas around both sides, so this matches a whole handle and not a fragment of one: without them
   * "serum-natural-white" would also answer to "natural-white".
   */
  function handleListed(list, needle) {
    if (!list || !needle) return false;
    return (',' + list + ',').indexOf(',' + needle + ',') !== -1;
  }

  /**
   * Which device position carries the second-device rate, as a handle, or '' when none does.
   *
   * Not ours to choose. The rate behind it is a BXGY over both devices: one unit is consumed as the prerequisite and
   * one unit is discounted, and Shopify picks the CHEAPEST eligible unit. Measured at the live cart: a GERO G3 (8990)
   * next to a Munddusche (7990) leaves the G3 at full price and cuts the Munddusche to 5993.
   *
   * The rule this replaced was "every device that is not the base device". That agreed with the cart only on the G3
   * configurator, where the base happens to be the dearer device. On the A2 configurator it predicted the cut on the
   * G3 while the cart put it on the Munddusche, so the cart came out 2,50 EUR ABOVE the page, which is the dangerous
   * direction. Predicting the cheapest device is what makes both configurators agree.
   *
   * `positions` is [{ handle, unit, planId }] for everything configured, `unit` being the price the line will really
   * carry. A position on a selling plan is no candidate: a BXGY never touches a line that carries one.
   */
  function discountedDeviceFor(data, positions) {
    var rules = (data && data.discounts) || {};

    var devices = (positions || []).filter(function (position) {
      return !position.planId && handleListed(rules.deviceHandles, position.handle);
    });

    /* Gezaehlt werden EINHEITEN, nicht Positionen. Zwei Stueck desselben Geraets auf einer Zeile erfuellen die
       Bedingung genauso wie zwei verschiedene Geraete: eine Einheit traegt die Bedingung, eine wird rabattiert. Am
       Warenkorb geprueft, zweimal dieselbe G3 laesst die zweite auf 6743 fallen. */
    var units = 0;
    devices.forEach(function (position) {
      units += position.qty || 1;
    });
    if (units < 2) return '';

    var cheapest = devices[0];
    devices.forEach(function (position) {
      if (position.unit < cheapest.unit) cheapest = position;
    });
    return cheapest.handle;
  }

  /**
   * What one position contributes to the total, after its own rate.
   *
   * The two rate families differ in REACH, and that difference is the whole reason this function exists:
   *
   *   - The second-device rate sits on a BXGY, and a BXGY discounts exactly one unit. A line carrying two devices
   *     therefore pays one discounted unit and one full one. Multiplying the discounted unit across the line would
   *     state a price below what the cart charges, which is the dangerous direction.
   *   - The set rates sit on product discounts without a quantity cap, so they reach every unit of the line.
   */
  function positionTotal(position, percent, isDeviceRate) {
    var qty = position.qty || 1;
    if (!percent || percent <= 0) return position.unit * qty;
    if (isDeviceRate) return discountedUnit(position.unit, percent) + position.unit * (qty - 1);
    return discountedUnit(position.unit, percent) * qty;
  }

  /**
   * The configurator's own discount rate for one position, in whole percent.
   *
   * It PREDICTS what the automatic discounts in Shopify will do; it does not decide it. Every rate here has to have a
   * matching discount in the shop, which is why the rates are section settings rather than literals, and why they
   * default to 0: a configurator that promises a saving the cart does not grant is exactly the defect the
   * quantity_bundle badges on product.json had.
   *
   * Two rules:
   *
   *   1. Of the devices, exactly the one `discountedDeviceFor` names gets the second-device rate. Every other device
   *      pays full, the base device included, and on a selling plan no device is discounted at all, because the
   *      discount behind the rate is a BXGY and a BXGY skips a line carrying a plan.
   *   2. Everything else gets the set rate, deep or standard by handle, on a subscription exactly as on a one-time
   *      purchase. The two discounts behind those rates are product discounts carrying appliesOnSubscription, and
   *      they keep calculating on the allocation price. Verified at the cart: a subscribed 1706 becomes 1536.
   */
  function discountPercentFor(data, handle, planId, discountedDevice) {
    var rules = (data && data.discounts) || {};

    if (handleListed(rules.deviceHandles, handle)) {
      if (planId) return 0;
      return handle === discountedDevice ? rules.secondDevicePercent || 0 : 0;
    }

    if (handleListed(rules.setDeepHandles, handle)) return rules.setDeepPercent || 0;
    return rules.setPercent || 0;
  }

  /**
   * One unit price after the configurator's discount, rounded the way Shopify rounds a percentage: the discount per
   * unit is floored, so the customer keeps the fraction of a cent. Mirrors the rounding note in product-info.liquid.
   */
  function discountedUnit(unit, percent) {
    if (!percent || percent <= 0 || !unit) return unit;
    return unit - Math.floor((unit * percent) / 100);
  }

  function cheapestUnitPrice(data, handle, pinnedCount, aboApplies) {
    var pair = cheapestUnitPair(data, handle, pinnedCount, aboApplies);
    return pair === null ? null : pair.price;
  }

  /**
   * The cheapest reachable price AND the comparable price of the same variant.
   *
   * One pass, one winner, both numbers. Resolving the comparable afterwards would allow it to come from a different
   * variant than the one that set the floor, which is how a struck price ends up next to an amount it has nothing to
   * do with. Mirrors the `emit` argument of snippets/zh-konf-cheapest.liquid, and the two must agree, otherwise the
   * pair jumps the moment the controller takes over.
   *
   * "Comparable" is the catalogue compare_at where there is one, otherwise the one-time price. The second half is what
   * makes a subscription count as a saving: on this order the customer really does pay the allocation instead.
   */
  function cheapestUnitPair(data, handle, pinnedCount, aboApplies) {
    var product = data.products && data.products[handle];
    if (!product || !product.variants) return null;

    var best = null;
    var fallback = null;

    function reference(variant) {
      return variant.compareAt > variant.price ? variant.compareAt : variant.price;
    }

    product.variants.forEach(function (variant) {
      if (pinnedCount && product.hasCount && variant.count !== pinnedCount) return;

      var candidate = {
        price: aboApplies ? cheapestPlanPrice(variant) : variant.price,
        compare: reference(variant)
      };
      if (fallback === null || candidate.price < fallback.price) fallback = candidate;
      if (!variant.available) return;
      if (best === null || candidate.price < best.price) best = candidate;
    });

    var winner = best === null ? fallback : best;
    if (winner === null) return null;

    /* Never below the price it charges: a compare_at under its own price would strike through the smaller number. */
    return {
      price: winner.price,
      compare: winner.compare < winner.price ? winner.price : winner.compare
    };
  }

  /**
   * "ab" price of a preset: the cheapest total the customer can still reach with this set.
   *
   * A floor, not the price of the preset's default configuration. "ab X" is only honest if X is actually reachable, and
   * a floor is the one value that can never be undercut by a later choice, which is also why it never has to move once
   * rendered. Set membership is fixed here: every configured position is priced, because dropping one would no longer be
   * this set.
   */
  function presetFloorPrice(data, preset, aboDefaultOn) {
    return presetTotals(data, preset, aboDefaultOn).total;
  }

  /**
   * The set's "ab" price and the comparable sum beside it, from one pass over the same positions.
   *
   * `compare` is the compare_at sum, the same reference the option cards and the rest of the shop use. `total` carries
   * the configurator's own discount on top, so the gap between the two is BOTH the catalogue markdown and the set
   * discount. That is what makes a set card read as the better deal: it saves everything a single article saves, plus
   * what the set adds.
   *
   * The consequence to keep in mind: without a set discount the two differ only by the catalogue markdown, and that
   * figure shrinks as positions with a smaller markdown join the set. The set discount has to be deep enough to
   * outrun it, otherwise the ladder falls instead of rising.
   *
   * Set membership is fixed here, exactly as in presetFloorPrice: every configured position is priced, because dropping
   * one would no longer be this set.
   */
  function presetTotals(data, preset, aboDefaultOn) {
    var total = 0;
    var compare = 0;
    /* Same sum as `total`, but without discountedUnit's per-position floor. Only ever feeds the advertised percentage,
       never a displayed price: see the note on savingPercent. */
    var exact = 0;

    /* Zwei Durchlaeufe, aus demselben Grund wie in computeTotals: welches Geraet den Zweitgeraetesatz bekommt, steht
       erst fest, wenn alle Positionen bepreist sind. */
    var positions = [];

    ((preset && preset.items) || []).forEach(function (item) {
      var owner = ownerGroupFor(data, item.handle);
      var aboApplies = Boolean(aboDefaultOn && owner && owner.allowAbo);
      var packFree = Boolean(owner && owner.chooseCount);

      var pair = cheapestUnitPair(data, item.handle, packFree ? '' : item.count, aboApplies);
      if (pair === null) return;

      /* `aboApplies` steht fuer eine Abo-Position, weil der Preis dann aus einer Allocation kommt. */
      positions.push({
        handle: item.handle,
        qty: item.qty || 1,
        planId: aboApplies ? 'abo' : '',
        unit: pair.price,
        reference: pair.compare
      });
    });

    var discountedDevice = discountedDeviceFor(data, positions);

    positions.forEach(function (position) {
      /* Dieselbe Regel wie in computeTotals, sonst nennt die Set-Karte einen anderen Preis als der Warenkorb zieht. */
      var percent = discountPercentFor(data, position.handle, position.planId, discountedDevice);
      var isDeviceRate = position.handle === discountedDevice;
      total += positionTotal(position, percent, isDeviceRate);
      compare += position.reference * position.qty;

      /* `exact` folgt derselben Reichweite wie `total`, nur ohne die Abrundung je Position. */
      var exactUnit = (position.unit * (100 - (percent > 0 ? percent : 0))) / 100;
      exact += isDeviceRate && percent > 0
        ? exactUnit + position.unit * (position.qty - 1)
        : exactUnit * position.qty;
    });

    return { total: total, compare: compare, exact: exact };
  }

  /**
   * Whole percent saved, rounded half up, or 0 when there is nothing to advertise.
   *
   * Rounded to nearest (decision October 2026): the per-product Seal rates carry two decimals, 18.81 shows as 19 and
   * 33.44 as 33, and every saving badge in the theme rounds the same way so one product never shows two numbers.
   *
   * `net` is the EXACT discounted sum where the caller has one, and `total` (the floored sum the customer actually
   * pays) otherwise. discountedUnit floors the cut per position, so a set whose positions all carry the same rate and
   * no catalogue markdown lands a hair under the configured percent; against the exact sum that configured rate is
   * what gets rounded.
   */
  function savingPercent(total, compare, net) {
    if (!(compare > total) || compare <= 0) return 0;
    var reference = typeof net === 'number' && net > 0 ? net : total;
    return Math.round(((compare - reference) / compare) * 100);
  }

  /**
   * "ab" price for the intro band: the cheapest configuration the customer can leave the configurator with at all.
   *
   * Differs from presetFloorPrice in one decisive way: membership is not fixed. A step the customer can skip contributes
   * nothing, because every optional single-choice step carries an opt-out row and every multi-choice step starts empty.
   * So the floor is the device plus only the steps that genuinely cannot be skipped. A gated step is absent from the
   * minimal configuration by definition, since nothing in it triggers the gate.
   */
  function configuratorFloorPrice(data, aboDefaultOn) {
    var total = cheapestUnitPrice(data, data.baseHandle, '', false);
    if (total === null) return 0;

    (data.groups || []).forEach(function (group) {
      if (group.onlyIfHandle) return;
      if (!group.required) return;
      if (group.selection === 'multi') return;

      var aboApplies = Boolean(aboDefaultOn && group.allowAbo);
      var pinned = group.chooseCount ? '' : group.defaultCount || '';

      var best = null;
      (group.products || []).forEach(function (handle) {
        var unit = cheapestUnitPrice(data, handle, pinned, aboApplies);
        if (unit === null) return;
        if (best === null || unit < best) best = unit;
      });

      if (best !== null) total += best * (group.qty || 1);
    });

    return total;
  }

  /**
   * The two money rows of the summary, derived so that they reconcile: subtotal minus saving equals the total the customer
   * pays.
   *
   * The subtotal is the sum BEFORE any saving, which is what a row called "Zwischensumme" followed by a "Du sparst" row
   * has to mean. Showing the final total there instead made the three numbers contradict each other: the subtotal equalled
   * the total while a saving was still deducted below it.
   *
   * With no saving there is nothing to deduct, so the subtotal is the total and the saving is zero, and the caller drops
   * the saving row entirely.
   */
  function summaryMoneyRows(totals) {
    var hasSaving = totals.compare > totals.total;
    return {
      subtotal: hasSaving ? totals.compare : totals.total,
      saving: hasSaving ? totals.compare - totals.total : 0
    };
  }

  /**
   * Saving per piece of every pack size of `handle`, measured against its SMALLEST pack.
   *
   * Derived from the catalogue, never configured. A pack is therefore advertised as cheaper exactly when its price per
   * piece really is lower, and while the packs are priced linearly the whole map comes back empty. Nothing here can
   * promise a saving the cart does not grant, which is the failure mode a hardcoded percentage has.
   *
   * Sold-out packs are skipped: a price nobody can buy is not a reference, the same rule cheapestUnitPrice follows.
   * The percentage is rounded to the nearest whole, because the exact prices sit in the summary right below.
   *
   * @returns {Object} count value -> whole percent saved, only for entries above zero
   */
  function packSavings(data, handle, color, counts) {
    var out = {};
    var units = packUnits(data, handle, color, counts);

    var refSize = 0;
    var refUnit = 0;
    Object.keys(units).forEach(function (count) {
      var size = parseInt(count, 10);
      if (refSize === 0 || size < refSize) {
        refSize = size;
        refUnit = units[count];
      }
    });

    if (!refUnit) return out;

    Object.keys(units).forEach(function (count) {
      var percent = Math.round(((refUnit - units[count]) / refUnit) * 100);
      if (percent > 0) out[count] = percent;
    });
    return out;
  }

  /**
   * Price per piece of every pack of `handle` the customer can actually buy in `color`.
   *
   * Split out of packSavings because two questions need the same answer and must not drift apart: what a pack saves,
   * and whether the product can be bought at all. The second decides whether a product gets a vote in
   * agreedPackSavings, and answering it with a second notion of "matching variant" is how the two would diverge.
   *
   * @returns {Object} count value -> price per piece in cents, only for purchasable packs
   */
  function packUnits(data, handle, color, counts) {
    var out = {};
    var product = data.products && data.products[handle];
    if (!product || !product.hasCount || !counts || !counts.length) return out;

    counts.forEach(function (count) {
      var size = parseInt(count, 10);
      if (!size || size < 1) return;
      var variant = resolveVariant(data, handle, color, count);
      /* resolveVariant falls back when the exact pack is missing, so verify we really got this pack. */
      if (!variant || !variant.available || variant.count !== count) return;
      out[count] = variant.price / size;
    });

    return out;
  }

  /**
   * The saving a group may advertise while the customer has picked nothing in it.
   *
   * "Ohne Bürstenkopf" is a real answer, and the pack ladder next to it still reads as an offer. Showing one
   * arbitrary product's ladder there would promise a discount only that product gives, so the badge appears only
   * where EVERY option the customer could still pick gives the SAME one. Where they differ there is no single true
   * number, and the badge stays away rather than picking a winner.
   *
   * A product nobody can buy in this colour gets no vote. It is not an option the customer has, so letting it veto
   * would hide a true statement because of one sold-out article. A product that IS buyable and simply prices its
   * packs linearly does veto, because choosing it would earn nothing.
   *
   * @returns {Object} count value -> whole percent saved, empty when the options disagree
   */
  function agreedPackSavings(data, handles, color, counts) {
    var agreed = null;

    for (var index = 0; index < (handles || []).length; index += 1) {
      if (!Object.keys(packUnits(data, handles[index], color, counts)).length) continue;

      var savings = packSavings(data, handles[index], color, counts);
      if (!Object.keys(savings).length) return {};
      if (agreed && !sameSavings(agreed, savings)) return {};
      agreed = savings;
    }

    return agreed || {};
  }

  function sameSavings(a, b) {
    var keys = Object.keys(a);
    if (keys.length !== Object.keys(b).length) return false;
    return keys.every(function (key) {
      return a[key] === b[key];
    });
  }

  /**
   * The subscription interval a group should show, given the pack size the customer picked.
   *
   * A 9er pack is 18 months of brush heads. Delivering it every 2 months is a cancellation waiting to happen, so the
   * PACK SIZE picks the interval: pieces times the group's months per piece is how long the pack lasts, and the longest
   * plan that does not exceed that wins. Brush heads are changed every 2 months, so 3 pieces every 6 months, 6 and 9
   * every 9 (Seal has no 12 or 18). Without months per piece a piece counts as one month, 3 every 3, 6 every 6, 9 every
   * 9, which is what the nozzles keep. Households that go through heads faster keep every plan to pick by hand.
   *
   * Only the SUGGESTION follows the pack. Once the customer has touched the interval themselves, `touched` keeps their
   * choice, and a still-valid previous selection is never overridden either. Falling back in that order matters: the
   * merchant's default_interval outranks "whatever came first" but not the customer.
   *
   * Above the pack size sits the plan Seal marks as recommended ("6 Monate (Am beliebtesten)"): it is always the
   * preselection until the customer picks an interval themselves. The pack size only suggests an interval for products
   * without that marker. The marker lives in the plan's option value, so it is matched on the label.
   *
   * @param {Array}  available - purchasable plans, [{ id, label }]
   * @param {String} count - the selected pack size, e.g. "9"
   * @param {String} defaultInterval - the group's configured fallback, e.g. "6"
   * @param {String} previous - plan id currently selected, if any
   * @param {Boolean} touched - whether the customer changed the interval themselves
   * @param {String} monthsPerPiece - how long one piece lasts, e.g. "2" for brush heads; blank or invalid = 1
   */
  function preferredPlanId(available, count, defaultInterval, previous, touched, monthsPerPiece) {
    if (!available || !available.length) return '';

    var stillValid = available.some(function (plan) {
      return String(plan.id) === String(previous);
    });
    if (stillValid && touched) return String(previous);

    var popular = '';
    available.forEach(function (plan) {
      if (!popular && String(plan.label).toLowerCase().indexOf(POPULAR_PLAN_MARKER) !== -1) popular = String(plan.id);
    });
    if (popular) return popular;

    if (stillValid && !count) return String(previous);

    function byInterval(months) {
      var found = '';
      available.forEach(function (plan) {
        /* Seal writes the option value as "6 Monate", so the leading number is the interval. */
        if (!found && String(plan.label).indexOf(String(months)) === 0) found = String(plan.id);
      });
      return found;
    }

    /* The longest plan that is not longer than the pack lasts, so the next delivery never comes after it ran out. */
    function longestWithin(months) {
      var found = '';
      var foundMonths = 0;
      available.forEach(function (plan) {
        var planMonths = parseInt(String(plan.label), 10);
        if (planMonths > 0 && planMonths <= months && planMonths > foundMonths) {
          found = String(plan.id);
          foundMonths = planMonths;
        }
      });
      return found;
    }

    var perPiece = parseInt(monthsPerPiece, 10);
    if (!(perPiece > 0)) perPiece = 1;
    var pieces = parseInt(count, 10);
    var matched = pieces > 0 ? longestWithin(pieces * perPiece) : '';
    if (matched) return matched;
    if (stillValid) return String(previous);

    var fallback = defaultInterval ? byInterval(defaultInterval) : '';
    return fallback || String(available[0].id);
  }

  /**
   * Seal's usage hint for a plan ("Wir empfehlen die Düse alle 3 Monate zu wechseln."), maintained in Seal as the
   * selling plan description. Blank and whitespace-only descriptions count as no hint, so the step never shows an empty
   * line or points aria-describedby at nothing.
   *
   * @param {Object} plan - entry of product.plans, { id, label, saving, description }, or null
   * @returns {String} the trimmed hint, '' when there is none
   */
  function planHint(plan) {
    if (!plan || typeof plan.description !== 'string') return '';
    return plan.description.trim();
  }

  /**
   * The shipping half of the tax note: "zzgl. Versand" while the total stays below the free-shipping threshold,
   * "versandkostenfrei" from the threshold on. A threshold of 0 means shipping is free regardless, which is the case
   * for a logged-in customer.
   *
   * @param {Number} threshold - free-shipping threshold in cents, 0 when shipping is always free
   * @param {Number} total - configured total in cents
   * @param {Object} labels - { plusShipping, freeShipping }
   * @returns {String} the matching label, '' when it is missing
   */
  function shippingNote(threshold, total, labels) {
    var underThreshold = threshold > 0 && total < threshold;
    var label = underThreshold ? (labels || {}).plusShipping : (labels || {}).freeShipping;
    return label || '';
  }

  var api = {
    createMoneyFormatter: createMoneyFormatter,
    planHint: planHint,
    shippingNote: shippingNote,
    resolveVariant: resolveVariant,
    unitPriceFor: unitPriceFor,
    cheapestPlanPrice: cheapestPlanPrice,
    preferredPlanId: preferredPlanId,
    packSavings: packSavings,
    agreedPackSavings: agreedPackSavings,
    applyPreset: applyPreset,
    ownerGroupFor: ownerGroupFor,
    activeHandles: activeHandles,
    applySwap: applySwap,
    groupVisible: groupVisible,
    previewHandle: previewHandle,
    buildItems: buildItems,
    computeTotals: computeTotals,
    summaryMoneyRows: summaryMoneyRows,
    cheapestUnitPrice: cheapestUnitPrice,
    cheapestUnitPair: cheapestUnitPair,
    discountedDeviceFor: discountedDeviceFor,
    positionTotal: positionTotal,
    discountPercentFor: discountPercentFor,
    discountedUnit: discountedUnit,
    presetTotals: presetTotals,
    savingPercent: savingPercent,
    presetFloorPrice: presetFloorPrice,
    configuratorFloorPrice: configuratorFloorPrice
  };

  if (typeof window !== 'undefined') {
    window.__zhKonf = window.__zhKonf || api;
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * DOM wiring
   * ---------------------------------------------------------------------------------------------------------------- */

  if (typeof document === 'undefined') return;

  function initRoot(root) {
    if (root.dataset.zhKonfInit === 'true') return;
    root.dataset.zhKonfInit = 'true';

    var dataEl = root.querySelector('[data-zh-konf-data]');
    if (!dataEl) return;

    var data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (error) {
      return;
    }
    if (!data.baseHandle || !data.products || !data.products[data.baseHandle]) return;

    var form = document.getElementById(data.formId);
    var aboDefaultOn = root.dataset.zhAboDefault === 'true';
    var discountCode = root.dataset.zhDiscountCode || '';
    var formatMoney = createMoneyFormatter(data.moneyFormat);

    var el = {
      idInput: form ? form.querySelector('[data-zh-id-input]') : null,
      presetInputs: Array.prototype.slice.call(root.querySelectorAll('[data-zh-preset]')),
      colorInputs: Array.prototype.slice.call(root.querySelectorAll('[data-zh-color-input]')),
      baseOptionInputs: Array.prototype.slice.call(root.querySelectorAll('[data-zh-base-option]')),
      optionInputs: Array.prototype.slice.call(root.querySelectorAll('[data-zh-option]')),
      countInputs: Array.prototype.slice.call(root.querySelectorAll('[data-zh-count-input]')),
      aboInputs: Array.prototype.slice.call(root.querySelectorAll('[data-zh-abo-input]')),
      intervals: Array.prototype.slice.call(root.querySelectorAll('[data-zh-interval]')),
      steps: Array.prototype.slice.call(root.querySelectorAll('[data-zh-step]')),
      groupSteps: Array.prototype.slice.call(root.querySelectorAll('[data-zh-group-step]')),
      mediaItems: Array.prototype.slice.call(root.querySelectorAll('[data-zh-media]')),
      introPrice: root.querySelector('[data-zh-intro-price]'),
      barPrice: root.querySelector('[data-zh-bar-price]'),
      barCompare: root.querySelector('[data-zh-bar-compare]'),
      barSummary: root.querySelector('[data-zh-bar-summary]'),
      barSaving: root.querySelector('[data-zh-bar-saving]'),
      barShipping: root.querySelector('[data-zh-bar-shipping]'),
      chipText: root.querySelector('[data-zh-chip-text]'),
      sumRows: root.querySelector('[data-zh-sum-rows]'),
      sumTitle: root.querySelector('[data-zh-sum-title]'),
      sumMeta: root.querySelector('[data-zh-sum-meta]'),
      sumMedia: Array.prototype.slice.call(root.querySelectorAll('[data-zh-sum-media]')),
      sumStatus: root.querySelector('[data-zh-sum-status]'),
      error: root.querySelector('[data-zh-error]'),
      submitWrap: root.querySelector('[data-zh-submit-wrap]')
    };
    var submitButton = el.submitWrap ? el.submitWrap.querySelector('button') : null;
    var submitLabel = el.submitWrap ? el.submitWrap.querySelector('[data-zh-submit-label]') : null;
    var busy = false;

    /* ---- state ---- */

    var defaultColor = '';
    (data.colors || []).forEach(function (color) {
      if (!defaultColor && color.selected) defaultColor = color.name;
    });
    if (!defaultColor && data.colors && data.colors.length) defaultColor = data.colors[0].name;

    var state = {
      color: defaultColor,
      presetKey: null,
      intervalTouched: {},
      baseQty: 1,
      baseOptions: {},
      groups: {},
      extras: [],
      /* Key of the group whose selection currently owns the preview frame, null when the set photo has it back. */
      mediaGroup: null
    };

    function groupByKey(key) {
      var found = null;
      (data.groups || []).forEach(function (group) {
        if (group.key === key) found = group;
      });
      return found;
    }

    /* Seed from what Liquid rendered, so the first paint and the first state agree. */
    (data.groups || []).forEach(function (group) {
      var checked = null;
      el.optionInputs.forEach(function (input) {
        if (input.name !== 'zh-group-' + group.key) return;
        /* The opt-out row is a real radio with an empty handle, so it must not seed a selection. */
        if (input.checked && !checked && input.dataset.zhHandle) checked = input.dataset.zhHandle;
      });
      if (checked) {
        state.groups[group.key] = {
          handles: [checked],
          count: group.defaultCount || '',
          qty: group.qty || 1,
          planId: ''
        };
      }
    });

    el.baseOptionInputs.forEach(function (input) {
      if (!input.checked) return;
      var names = data.products[data.baseHandle].optionNames || [];
      var name = names[parseInt(input.dataset.zhPosition, 10) - 1];
      if (name) state.baseOptions[name] = input.value;
    });

    /* ---- rendering ---- */

    function syncColorVisibility() {
      Array.prototype.slice
        .call(root.querySelectorAll('[data-zh-card-media],[data-zh-preset-media]'))
        .forEach(function (node) {
          var nodeColor = node.dataset.zhColor || '';
          node.classList.toggle('is-active', nodeColor === '' || nodeColor === state.color);
        });
    }

    function syncCards() {
      (data.groups || []).forEach(function (group) {
        var swapActive = Boolean(
          group.swapIfHandle && activeHandles(data, state).indexOf(group.swapIfHandle) !== -1
        );

        var swapSlot = root.querySelector('[data-zh-swap-slot="' + group.key + '"]');
        if (swapSlot) swapSlot.hidden = !swapActive;

        /*
          The rule decides the group's handles and the checkboxes follow. It used to be the
          other way round, patched inline while walking the inputs, and that is how the
          incoming product came to survive the rule going inactive: its card sits inside the
          slot hidden one line above, so nothing ever unchecked it. The selection stayed,
          invisible and unremovable, and it was still charged.
        */
        if (group.swapInHandle) {
          var selection = state.groups[group.key];
          var before = (selection && selection.handles) || [];
          var after = applySwap(before, group, swapActive);
          var changed = after.length !== before.length || after.some(function (handle, i) {
            return handle !== before[i];
          });

          if (changed) {
            state.groups[group.key] = selection || { handles: [], qty: 1, planId: '' };
            state.groups[group.key].handles = after;
          }

          /* Both halves follow state, so the DOM can no longer disagree with the order. */
          [group.swapOutHandle, group.swapInHandle].forEach(function (handle) {
            if (!handle) return;
            var box = root.querySelector(
              '[data-zh-option][name="zh-group-' + group.key + '"][data-zh-handle="' + handle + '"]'
            );
            if (box) box.checked = after.indexOf(handle) !== -1;
          });
        }

        el.optionInputs.forEach(function (input) {
          if (input.name !== 'zh-group-' + group.key) return;
          var handle = input.dataset.zhHandle;
          /* The opt-out row has no product: nothing to price, and it must never be disabled as "sold out". */
          if (!handle) return;
          var card = root.querySelector('[data-zh-card="' + handle + '"]');

          /* Only the card's visibility is left here; the selection itself is settled above. */
          var hiddenBySwap = swapActive && group.swapOutHandle === handle;
          /*
            Hide the slot, not the label. The paired sr-only input lives in the same slot, so hiding
            the label alone left a card that was invisible but still tab- and arrow-key reachable.

            Resolved from the input, not from the label, because the input is the half that must not
            stay reachable. There is deliberately no fallback to the label: hiding a label on its own
            is the exact bug the slot exists to prevent, so a missing slot has to fail as a visible
            duplicate card rather than as an invisible one the customer still gets charged for.
          */
          var slot = input.closest('[data-zh-card-slot]');
          if (slot) slot.hidden = hiddenBySwap;

          /* Availability is per resolved colour, so a card can flip between usable and sold out on a colour change. */
          var selection = state.groups[group.key] || {};
          var variant = resolveVariant(data, handle, state.color, selection.count || group.defaultCount || '', null);
          var unavailable = !variant || !variant.available;
          /*
            The swapped-out option is disabled as well as hidden. `hidden` only takes effect through a
            stylesheet and an author rule can undo it; `disabled` removes the input from the tab order
            and from the form itself, so the option stays unreachable whatever happens to the markup.
          */
          input.disabled = unavailable || hiddenBySwap;

          if (card) {
            card.classList.toggle('is-disabled', unavailable);
            var note = card.querySelector('[data-zh-card-note]');
            if (note) note.hidden = !unavailable;
            /*
              The price and the struck-through price are NOT touched here. They carry the server-rendered "ab" floor,
              which is colour- and pack-independent by definition, so rewriting them from the currently resolved variant
              would replace a floor with a live price and reintroduce the flicker the floor exists to remove.
            */
          }

          /* A selection that just went unavailable must not survive as a phantom line. */
          if (unavailable && input.checked) {
            input.checked = false;
            if (state.groups[group.key]) {
              state.groups[group.key].handles = (state.groups[group.key].handles || []).filter(function (other) {
                return other !== handle;
              });
            }
          }
        });
      });
    }

    function syncAbo() {
      (data.groups || []).forEach(function (group) {
        if (!group.allowAbo) return;
        var stepEl = root.querySelector('[data-zh-abo-step="' + group.key + '"]');
        if (!stepEl) return;

        var selection = state.groups[group.key];
        var handle = selection && selection.handles ? selection.handles[0] : null;
        var product = handle ? data.products[handle] : null;
        var variant = handle
          ? resolveVariant(data, handle, state.color, selection.count || group.defaultCount || '', null)
          : null;
        var plans = product ? product.plans || [] : [];

        /* No purchasable plan for this choice means no subscription step at all. */
        var available = [];
        if (variant && variant.allocations) {
          available = plans.filter(function (plan) {
            return Object.prototype.hasOwnProperty.call(variant.allocations, String(plan.id));
          });
        }
        if (!available.length) {
          stepEl.hidden = true;
          if (selection) selection.planId = '';
          return;
        }
        stepEl.hidden = !groupVisible(data, state, group);

        var select = stepEl.querySelector('[data-zh-interval]');
        var aboInput = stepEl.querySelector('[data-zh-abo-on]');

        /* Rebuild the interval list for the current product: the sets differ per product. */
        if (select) {
          var wanted = preferredPlanId(
            available,
            (selection && selection.count) || group.defaultCount || '',
            group.defaultInterval,
            select.value,
            Boolean(state.intervalTouched[group.key]),
            group.monthsPerPiece
          );

          var options = available.map(function (plan) {
            var option = document.createElement('option');
            option.value = String(plan.id);
            option.textContent = plan.label;
            if (String(plan.id) === wanted) option.selected = true;
            return option;
          });
          select.replaceChildren.apply(select, options);
          if (aboInput) aboInput.value = wanted;
        }

        if (selection) selection.planId = aboInput && aboInput.checked ? aboInput.value : '';

        var allocation = aboInput ? variant.allocations[aboInput.value] : null;
        var aboPrice = stepEl.querySelector('[data-zh-abo-price]');
        if (aboPrice && allocation) aboPrice.textContent = allocation.priceFormatted;

        /* Der Streichpreis ist der Einmalpreis derselben Variante, also genau der Betrag, den der Kunde ohne Abo auf
           dieser Bestellung zahlen wuerde. Er folgt dem Preis, damit die beiden nicht auseinanderlaufen, wenn die
           Gruppe auf ein anderes Produkt oder eine andere Packgroesse wechselt. */
        var aboCompare = stepEl.querySelector('[data-zh-abo-compare]');
        if (aboCompare) {
          if (allocation && variant && allocation.price < variant.price) {
            aboCompare.textContent = variant.priceFormatted;
            aboCompare.hidden = false;
          } else {
            aboCompare.textContent = '';
            aboCompare.hidden = true;
          }
        }
        var oneTimePrice = stepEl.querySelector('[data-zh-abo-onetime-price]');
        if (oneTimePrice) oneTimePrice.textContent = variant.priceFormatted;

        /*
          The plan group name and the saving belong to the product selected in the group above. Liquid can only render
          one product's values on first paint, so both have to be restated here: a group may offer products from
          different plan groups at different discounts, and a stale percentage is a saving the cart never granted.
        */
        var selectedPlan = null;
        available.forEach(function (plan) {
          if (aboInput && String(plan.id) === String(aboInput.value)) selectedPlan = plan;
        });

        var aboName = stepEl.querySelector('[data-zh-abo-name]');
        if (aboName && product && product.planGroupName) aboName.textContent = product.planGroupName;

        /* The subline names the product the subscription is for. Only the name is
           rewritten, not the whole line: the prefix carries the article ("für deinen")
           and belongs to the group, not to the choice inside it. */
        var aboForName = stepEl.querySelector('[data-zh-abo-for-name]');
        if (aboForName && product && product.title) aboForName.textContent = product.title;

        /* Seal's usage hint follows the selected plan, so switching from Bürstenkopf to another product in the same
           group does not leave the previous product's advice behind. The select only references the hint while it has
           text, matching subscription-picker.liquid. */
        var aboHint = stepEl.querySelector('[data-zh-abo-hint]');
        if (aboHint) {
          var hintText = planHint(selectedPlan);
          aboHint.textContent = hintText;
          aboHint.hidden = !hintText;
          if (select) {
            if (hintText) {
              select.setAttribute('aria-describedby', aboHint.id);
            } else {
              select.removeAttribute('aria-describedby');
            }
          }
        }

        var aboSaving = stepEl.querySelector('[data-zh-abo-saving]');
        if (aboSaving) {
          var percent = selectedPlan ? selectedPlan.saving : 0;
          if (percent > 0) {
            /* Non-breaking space before the sign, matching how Liquid renders it. */
            aboSaving.textContent = (data.aboSavingsLabel || 'Spare') + ' ' + percent + ' %';
            aboSaving.hidden = false;
          } else {
            aboSaving.hidden = true;
          }
        }

        var surcharge = stepEl.querySelector('[data-zh-abo-surcharge]');
        if (surcharge) {
          var delta = allocation ? variant.price - allocation.price : 0;
          surcharge.textContent = delta > 0 ? data.aboSurchargeLabel + ' +' + formatMoney(delta) : '';
        }
      });
    }

    function syncVisibleSteps() {
      var visible = [];

      el.steps.forEach(function (stepEl) {
        var groupKey = stepEl.dataset.zhGroupStep || stepEl.dataset.zhAboStep || '';
        if (groupKey) {
          var group = groupByKey(groupKey);
          if (group && !groupVisible(data, state, group)) {
            stepEl.hidden = true;
          } else if (stepEl.dataset.zhGroupStep) {
            /* Abo steps stay under syncAbo's control: it hides them when no plan applies. */
            stepEl.hidden = false;
          }
        }
        if (!stepEl.hidden) visible.push(stepEl);
      });

      /* The last VISIBLE step drops its bottom border: .zh-pdp__config already draws the
         rule that closes the two-column block, and both together render as one 2px line.
         It has to be the last visible one, not the last in the DOM, because a gated step
         at the end is hidden and would take the exemption with it. The CSS carries a
         :last-of-type fallback for the frame before this runs and for no JS at all. */
      el.steps.forEach(function (stepEl) {
        stepEl.classList.remove('zh-pdp__step--last');
      });
      if (visible.length) visible[visible.length - 1].classList.add('zh-pdp__step--last');

      visible.forEach(function (stepEl, index) {
        /* zh-pdp__step-num, not zh-konf__step-num: no markup ever carried the latter, so the badge kept whatever number
           the server had rendered while the counter beside it was corrected. A gated step consumes no server-side number
           and therefore repeated the previous step's badge once it became visible. */
        var num = stepEl.querySelector('.zh-pdp__step-num');
        if (num) num.textContent = String(index + 1);
        var counter = stepEl.querySelector('[data-zh-step-counter]');
        if (counter) {
          counter.textContent = (data.stepLabel || 'Schritt') + ' ' + (index + 1) + ' / ' + visible.length;
        }
      });
    }

    /**
     * Hydrates an option layer on first use. Those layers ship as a template (see zh-konf-media-layers.liquid), so the
     * page downloads a picture only once a step actually asks for it. Every other layer is already a real image and
     * returns straight away.
     */
    function mediaImage(item) {
      var existing = item.querySelector('img');
      if (existing) return existing;
      var template = item.querySelector('[data-zh-media-tpl]');
      if (!template || !template.content) return null;
      item.appendChild(template.content.cloneNode(true));
      template.parentNode.removeChild(template);
      return item.querySelector('img');
    }

    /* Bumped on every swap, so a picture that finishes loading after the customer has moved on is discarded. */
    var mediaToken = 0;

    function syncMedia() {
      if (!el.mediaItems.length) return;
      var target = null;
      var label = data.products[data.baseHandle].title;

      /* A step that owns the frame wins over everything else: while the customer is choosing nozzles the preview shows
         the nozzle they picked, not the set it came from. */
      var handle = previewHandle(state);
      if (handle) {
        target =
          root.querySelector(
            '[data-zh-media][data-zh-media-option="' + handle + '"][data-zh-media-color="' + state.color + '"]'
          ) || root.querySelector('[data-zh-media][data-zh-media-option="' + handle + '"]:not([data-zh-media-color])');
        if (target && data.products[handle]) label = data.products[handle].title;
      }

      if (!target && state.presetKey) {
        target = root.querySelector(
          '[data-zh-media][data-zh-media-preset="' + state.presetKey + '"][data-zh-media-color="' + state.color + '"]'
        );
      }
      if (!target) {
        target = root.querySelector(
          '[data-zh-media][data-zh-media-color="' +
            state.color +
            '"]:not([data-zh-media-preset]):not([data-zh-media-option])'
        );
      }
      if (!target) target = root.querySelector('[data-zh-media][data-zh-media-default]') || el.mediaItems[0];

      /* The chip names what the frame shows. It belongs to the picture, so it moves with it rather than with the
         summary. The preview bar that used to carry the same name is gone. */
      if (el.chipText) el.chipText.textContent = label;

      mediaToken += 1;
      var token = mediaToken;
      var image = mediaImage(target);

      function show() {
        if (token !== mediaToken) return;
        el.mediaItems.forEach(function (item) {
          item.classList.toggle('is-active', item === target);
        });
      }

      /* Swap only once the picture is decodable. Toggling first would drop the frame to its black ground for as long as
         a freshly hydrated layer takes to arrive, which reads as a flicker rather than as loading. */
      if (image && !image.complete) {
        image.addEventListener('load', show, { once: true });
        image.addEventListener('error', show, { once: true });
        return;
      }
      show();
    }

    /**
     * Writes every "ab" floor: the intro band and each preset card. Called ONCE, not from render(): a floor depends on
     * the catalogue and on the step configuration, never on the current selection, so recomputing it per change could
     * only ever make the number flicker without changing it.
     */
    function paintFloorPrices() {
      if (el.introPrice) {
        el.introPrice.textContent = formatMoney(configuratorFloorPrice(data, aboDefaultOn));
      }
      (data.presets || []).forEach(function (preset) {
        var node = root.querySelector('[data-zh-preset-price="' + preset.key + '"]');
        if (!node) return;

        var totals = presetTotals(data, preset, aboDefaultOn);
        node.textContent = formatMoney(totals.total);

        /* Streichpreis und Ersparnis folgen demselben Paar, damit die drei Zahlen auf der Karte nicht auseinanderlaufen
           koennen: der Prozentsatz gehoert genau zu der Differenz, die darueber steht. */
        var percent = savingPercent(totals.total, totals.compare, totals.exact);

        var compareNode = root.querySelector('[data-zh-preset-compare="' + preset.key + '"]');
        if (compareNode) {
          if (percent > 0) {
            compareNode.textContent = formatMoney(totals.compare);
            compareNode.hidden = false;
          } else {
            compareNode.textContent = '';
            compareNode.hidden = true;
          }
        }

        var savingNode = root.querySelector('[data-zh-preset-saving="' + preset.key + '"]');
        if (savingNode) {
          if (percent > 0) {
            savingNode.textContent = (data.aboSavingsLabel || 'Spare') + ' ' + percent + '\u00a0%';
            savingNode.hidden = false;
          } else {
            savingNode.textContent = '';
            savingNode.hidden = true;
          }
        }
      });
    }

    /**
     * The configuration as label/value pairs, in the order the customer walked through it. Derived from state rather
     * than from the DOM, so it cannot drift away from what buildItems will actually send to the cart.
     */
    function summaryRows() {
      var labels = data.labels || {};
      var rows = [];

      function describe(handle, count, qty) {
        var product = data.products[handle];
        if (!product) return null;
        var variant = resolveVariant(data, handle, state.color, count, null);
        var text = product.title;
        if (product.hasCount && variant && variant.count) text += ' · ' + variant.count;
        if ((qty || 1) > 1) text = qty + '× ' + text;
        return text;
      }

      if ((data.presets || []).length) {
        var preset = null;
        (data.presets || []).forEach(function (candidate) {
          if (candidate.key === state.presetKey) preset = candidate;
        });
        rows.push({ label: labels.set, value: preset ? preset.title : labels.custom });
      }

      if (state.color) rows.push({ label: labels.color, value: state.color });

      (data.groups || []).forEach(function (group) {
        if (!groupVisible(data, state, group)) return;
        var selection = state.groups[group.key];
        if (!selection || !(selection.handles || []).length) return;

        var described = selection.handles
          .map(function (handle) {
            return describe(handle, selection.count, selection.qty);
          })
          .filter(Boolean);
        if (described.length) rows.push({ label: group.title, value: described.join(', ') });

        /* One abo row per group that offers one, so a cart with two subscription lines is readable. */
        if (group.allowAbo) {
          var product = data.products[selection.handles[0]];
          var plan = null;
          if (product) {
            (product.plans || []).forEach(function (candidate) {
              if (String(candidate.id) === String(selection.planId)) plan = candidate;
            });
          }
          rows.push({ label: labels.abo, value: plan ? plan.label : labels.noAbo });
        }
      });

      state.extras.forEach(function (extra) {
        var text = describe(extra.handle, extra.count, extra.qty);
        if (text) rows.push({ label: labels.extra, value: text });
      });

      return rows;
    }

    function renderSummaryRows(totals) {
      if (!el.sumRows) return;
      var labels = data.labels || {};

      var nodes = [];
      summaryRows().forEach(function (row) {
        nodes.push(rowNode(row.label, row.value, ''));
      });
      var money = summaryMoneyRows(totals);
      nodes.push(rowNode(labels.subtotal, formatMoney(money.subtotal), 'zh-konf__row--total'));

      /* Only ever shown when the saving is real: a catalogue compare_at price or the abo difference. */
      if (money.saving > 0) {
        nodes.push(rowNode(labels.saving, '−' + formatMoney(money.saving), 'zh-konf__row--saving'));
      }

      el.sumRows.replaceChildren.apply(el.sumRows, nodes);
    }

    function rowNode(label, value, modifier) {
      var wrap = document.createElement('div');
      wrap.className = 'zh-konf__row' + (modifier ? ' ' + modifier : '');
      var dt = document.createElement('dt');
      dt.textContent = label || '';
      var dd = document.createElement('dd');
      dd.textContent = value == null ? '' : value;
      wrap.appendChild(dt);
      wrap.appendChild(dd);
      return wrap;
    }

    function syncSummary() {
      var totals = computeTotals(data, state);
      var priceText = formatMoney(totals.total);
      /* The intro band is deliberately NOT touched here: it carries the "ab" floor, which paintFloorPrices owns. */
      if (el.barPrice) el.barPrice.textContent = priceText;

      if (el.barCompare) {
        if (totals.compare > totals.total) {
          el.barCompare.textContent = formatMoney(totals.compare);
          el.barCompare.hidden = false;
        } else {
          el.barCompare.hidden = true;
        }
      }

      /* The saving under the price, from the same derivation the row list uses, so the
         two can never disagree: subtotal minus saving equals the total above. */
      if (el.barSaving) {
        var money = summaryMoneyRows(totals);
        if (money.saving > 0) {
          var savingLabel = (data.labels || {}).saving || '';
          el.barSaving.textContent = (savingLabel ? savingLabel + ' ' : '') + '−' + formatMoney(money.saving);
          el.barSaving.hidden = false;
        } else {
          el.barSaving.hidden = true;
        }
      }

      /* The shipping note follows the total across the free-shipping threshold: "zzgl. Versand"
         below it, "versandkostenfrei" from it on and always for a logged-in customer. It is
         written with its own comma because it joins the tax note inside one pair of brackets:
         "(inkl. MwSt., versandkostenfrei)". */
      if (el.barShipping) {
        var note = shippingNote(data.freeShippingThreshold || 0, totals.total, data.labels);
        el.barShipping.textContent = note ? ', ' + note : '';
      }

      /* The count no longer shows anywhere, but the screen-reader status below still says
         it: "Gesamt 106,96 €, 2 Artikel" tells someone who cannot see the row list how
         many lines that total covers. */
      var items = buildItems(data, state, 'preview');
      var itemCount = items.reduce(function (sum, item) {
        return sum + item.quantity;
      }, 0);

      var baseVariant = resolveVariant(data, data.baseHandle, state.color, '', state.baseOptions);
      var baseTitle = data.products[data.baseHandle].title;
      var parts = [baseTitle];
      if (state.color) parts.push(state.color);
      if (el.barSummary) el.barSummary.textContent = parts.join(' · ');
      if (el.idInput && baseVariant) el.idInput.value = baseVariant.id;

      /* The summary is headed by the SET when one is active, because that is the thing the customer picked. */
      var presetTitle = null;
      (data.presets || []).forEach(function (candidate) {
        if (candidate.key === state.presetKey) presetTitle = candidate.title;
      });
      if (el.sumTitle) el.sumTitle.textContent = presetTitle || baseTitle;
      if (el.sumMeta) {
        el.sumMeta.textContent = presetTitle ? parts.join(' · ') : state.color || '';
      }
      el.sumMedia.forEach(function (node) {
        var nodeColor = node.dataset.zhColor || '';
        node.classList.toggle('is-active', nodeColor === '' || nodeColor === state.color);
      });

      renderSummaryRows(totals);

      /*
        One short sentence for screen readers, in place of the row list re-announcing itself. Written unconditionally:
        assistive tech compares the text and stays silent when nothing actually changed.
      */
      if (el.sumStatus) {
        var labels = data.labels || {};
        el.sumStatus.textContent =
          (labels.total || 'Gesamt') + ' ' + priceText + ', ' + itemCount + ' ' + (labels.items || 'Artikel');
      }

      var soldOut = !baseVariant || !baseVariant.available;
      if (submitButton) submitButton.disabled = soldOut || items.length === 0;
      /* Not while busy: a step changed mid-flight would otherwise drop the button back to its idle label. */
      if (submitLabel && !busy) submitLabel.textContent = soldOut ? data.soldOutLabel : data.atcLabel;
    }

    function showError(message) {
      if (!el.error) return;
      el.error.textContent = message || 'Das hat leider nicht geklappt. Bitte versuch es erneut.';
      el.error.hidden = false;
    }

    function hideError() {
      if (el.error) el.error.hidden = true;
    }

    /**
     * Restates the pack-size saving badges for the product each group currently has selected.
     *
     * Liquid renders them for the group's DEFAULT product, which is right until the customer picks a different one in
     * the same step: two brush heads can carry different pack ladders, and a badge left over from the previous product
     * would state a saving this one does not give.
     *
     * With NOTHING picked, the badge falls back to what every option in the group agrees on. It used to fall back to
     * nothing at all, so a step whose products all save the same 5 and 10 percent went blank the moment the customer
     * chose its opt-out row, and it only looked right in steps that happen to have a default_product configured. The
     * ladder is worth reading before deciding, and where the options disagree there is no honest single number, so
     * agreedPackSavings hands back an empty map and the badges stay away.
     */
    function syncCountSavings() {
      (data.groups || []).forEach(function (group) {
        var wrap = root.querySelector('[data-zh-count-wrap="' + group.key + '"]');
        if (!wrap) return;

        var badges = Array.prototype.slice.call(wrap.querySelectorAll('[data-zh-count-saving]'));
        if (!badges.length) return;

        var selection = state.groups[group.key];
        var handle = (selection && selection.handles && selection.handles[0]) || group.defaultHandle || '';

        var counts = badges.map(function (badge) {
          return badge.dataset.zhCountValue || '';
        });
        var savings = handle
          ? packSavings(data, handle, state.color, counts)
          : agreedPackSavings(data, group.products || [], state.color, counts);

        badges.forEach(function (badge) {
          var percent = savings[badge.dataset.zhCountValue || ''];
          if (percent > 0) {
            /* Compact "-5%", the wording the theme's own quantity ladders use. Not aboSavingsLabel: that setting
               belongs to the subscription badges, where the word "Spare" carries the difference between a
               subscription and a one-time purchase. See the comment on the markup in zh-konf-steps.liquid. */
            badge.textContent = '-' + percent + '%';
            badge.hidden = false;
          } else {
            badge.textContent = '';
            badge.hidden = true;
          }
        });
      });
    }

    function render() {
      syncColorVisibility();
      syncCards();
      syncCountSavings();
      syncAbo();
      syncVisibleSteps();
      syncMedia();
      syncSummary();
      hideError();
    }

    /* ---- analytics ---- */

    function track(event, payload) {
      try {
        if (window.posthog && typeof window.posthog.capture === 'function') {
          window.posthog.capture(event, payload || {});
        }
      } catch (error) {
        /* Never let instrumentation break the purchase. */
      }
    }

    /* ---- events ---- */

    /**
     * Applies a preset to the state AND to the inputs. Shared by the change handler and by the initial paint, because a
     * preset rendered `checked` by Liquid otherwise leaves state.presetKey null: the card looks selected while the
     * summary says "eigene Zusammenstellung" and the cart property says "custom".
     */
    function selectPreset(presetKey) {
      var preset = null;
      (data.presets || []).forEach(function (candidate) {
        if (candidate.key === presetKey) preset = candidate;
      });
      if (!preset) return null;

      var applied = applyPreset(data, preset);
      state.presetKey = preset.key;
      state.baseQty = applied.baseQty;
      state.extras = applied.extras;

      /* Picking a set is a statement about the whole configuration, so the frame goes back to showing the set. On a
         phone nothing else would ever take the preview off the accessory the customer last tapped. */
      state.mediaGroup = null;

      /* Reflect the preset in the group inputs, so the customer sees what it chose and can still change it. */
      Object.keys(applied.groups).forEach(function (groupKey) {
        var group = groupByKey(groupKey);
        var incoming = applied.groups[groupKey];

        state.groups[groupKey] = {
          handles: incoming.handles,
          count: incoming.count || (group ? group.defaultCount : '') || '',
          qty: incoming.qty || 1,
          planId: state.groups[groupKey] ? state.groups[groupKey].planId : ''
        };

        var optedOut = incoming.handles.length === 0 && Boolean(group) && group.selection !== 'multi';

        el.optionInputs.forEach(function (option) {
          if (option.name !== 'zh-group-' + groupKey) return;
          var handle = option.dataset.zhHandle;
          /*
            The opt-out row is a real radio with an empty handle, and it is the visible state of an emptied
            single-choice step. Leaving it unchecked let a set that drops the second device paint a radio group with
            nothing selected at all.
          */
          option.checked = handle ? incoming.handles.indexOf(handle) !== -1 : optedOut;
        });
        el.countInputs.forEach(function (count) {
          if (count.dataset.zhGroup !== groupKey) return;
          if (incoming.count && count.value === incoming.count) count.checked = true;
        });
      });

      return preset;
    }

    el.presetInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        if (!input.checked) return;
        var preset = selectPreset(input.dataset.zhPresetKey);
        if (!preset) return;
        render();
        track('konfigurator_preset_selected', { preset: preset.title });
      });
    });

    el.colorInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        if (!input.checked) return;
        state.color = input.value;
        render();
      });
    });

    el.baseOptionInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        if (!input.checked) return;
        var names = data.products[data.baseHandle].optionNames || [];
        var name = names[parseInt(input.dataset.zhPosition, 10) - 1];
        if (name) state.baseOptions[name] = input.value;
        render();
      });
    });

    el.optionInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        var groupKey = input.name.replace('zh-group-', '');
        var group = groupByKey(groupKey);
        if (!group) return;

        var selection = state.groups[groupKey] || {
          handles: [],
          count: group.defaultCount || '',
          qty: group.qty || 1,
          planId: ''
        };

        var picked = input.dataset.zhHandle || '';

        if (group.selection === 'multi') {
          var handles = (selection.handles || []).filter(function (handle) {
            return handle !== picked;
          });
          if (input.checked && picked) handles.push(picked);
          selection.handles = handles;
        } else {
          /* Empty handle means the opt-out row: clear the group rather than store a blank selection. */
          selection.handles = input.checked && picked ? [picked] : [];
        }

        state.groups[groupKey] = selection;

        /* Touching a step hands it the preview, which is what makes the frame answer a tap on a phone too: there the
           media column is not sticky, so the scroll observer stays off and this is the only handover there is. */
        state.mediaGroup = groupKey;

        /* A manual change means the cart no longer matches the preset it started from. */
        state.presetKey = null;
        el.presetInputs.forEach(function (preset) {
          preset.checked = false;
        });

        render();
      });
    });

    el.countInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        if (!input.checked) return;
        var groupKey = input.dataset.zhGroup;
        if (!state.groups[groupKey]) return;
        state.groups[groupKey].count = input.value;
        render();
      });
    });

    el.aboInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        if (!input.checked) return;
        var groupKey = input.dataset.zhGroup;
        if (state.groups[groupKey]) state.groups[groupKey].planId = input.value;
        render();
      });
    });

    el.intervals.forEach(function (select) {
      select.addEventListener('change', function () {
        var groupKey = select.dataset.zhGroup;
        /* From here the pack size no longer moves this group's interval: the customer has stated one. */
        state.intervalTouched[groupKey] = true;
        var stepEl = root.querySelector('[data-zh-abo-step="' + groupKey + '"]');
        var aboInput = stepEl ? stepEl.querySelector('[data-zh-abo-on]') : null;
        if (!aboInput) return;

        aboInput.value = select.value;
        /* Picking a frequency implies wanting the subscription, the way Seal behaves. */
        aboInput.checked = true;
        if (state.groups[groupKey]) state.groups[groupKey].planId = select.value;
        render();
      });
    });

    /* ---- add to cart ---- */

    /* Holds the label the button carried before the add, so releasing restores it verbatim instead of guessing whether
       the state was "in den Warenkorb" or "ausverkauft". */
    var labelBeforeSubmit = null;

    /** Re-arms the buy button. Only ever called where the cart is known NOT to have changed, or is known to be shown. */
    function releaseSubmit() {
      busy = false;
      if (submitButton) submitButton.removeAttribute('aria-busy');
      if (submitLabel && labelBeforeSubmit !== null) {
        submitLabel.textContent = labelBeforeSubmit;
        labelBeforeSubmit = null;
      }
    }

    /**
     * Everything that happens after the cart was already mutated: the optional discount, reading the cart back, and
     * handing it to the theme so the drawer opens.
     *
     * This deliberately never surfaces a retryable error. The add is the commit point, so a failure here means the lines
     * ARE in the cart and re-enabling the button would invite a second add of every one of them. `_zh_konf_id` is unique
     * per attempt, so Shopify could not even merge the duplicates into the existing lines: the customer would end up
     * with two of everything. Falling through to the cart page shows the true state instead.
     */
    function finishAfterAdd(addJson, items) {
      track('konfigurator_add_to_cart', {
        preset: state.presetKey || 'custom',
        color: state.color,
        line_items: items.length
      });

      /*
        Die Konfigurator-Rabatte haengen KEINEN Code mehr an. Sie sind automatische Rabatte in Shopify, sie greifen
        also am Warenkorb von allein.

        Vorher ging je ein Code je zutreffender Regel raus. Das war aus zwei Gruenden falsch. Der Zweitgeraeterabatt
        und die beiden Set-Rabatte waren BXGY, und ein BXGY feuert erst ab der VOLLEN Gets-Menge: "buy 1, get 2" bei
        einer einzigen passenden Position ergab nichts, der Code blieb liegen und Shopify zeigte ihn dem Kunden als
        ungueltig an. Zweitens belegten die Codes den Rabattcode-Platz, obwohl der Kunde vielleicht mit einem
        Kampagnencode kam.

        Der Einzel-Code aus data-zh-discount-code bleibt, der ist eine eigene Funktion der Section und hat mit den
        Konfigurator-Saetzen nichts zu tun.
      */
      var applied = discountCode
        ? fetch('/discount/' + encodeURIComponent(discountCode)).catch(function () {})
        : Promise.resolve();

      return applied
        .then(function () {
          return fetch(data.cartJsUrl);
        })
        .then(function (response) {
          return response.json();
        })
        .then(function (cart) {
          cart.sections = addJson.sections;

          if (form) {
            form.dispatchEvent(
              new CustomEvent('variant:add', { bubbles: true, detail: { items: addJson.items, cart: cart } })
            );
          }
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart: cart } })
          );

          var themeSettings = window.themeVariables && window.themeVariables.settings;
          if (!form || !themeSettings || themeSettings.cartType !== 'drawer') {
            window.location.href = data.cartUrl;
            return;
          }

          /* The drawer is open, so the customer can see the result. A further click is now a deliberate second set. */
          releaseSubmit();
        })
        .catch(function () {
          /* Cart is correct, only the drawer refresh failed. Show the real cart rather than an error over a done deal. */
          window.location.href = data.cartUrl;
        });
    }

    function submit() {
      if (busy) return;

      var configId = 'zhk-' + Math.random().toString(36).slice(2, 10);
      var items = buildItems(data, state, configId);
      if (!items.length) {
        showError(data.soldOutLabel);
        return;
      }

      busy = true;
      if (submitButton) submitButton.setAttribute('aria-busy', 'true');
      if (submitLabel && data.atcBusyLabel) {
        labelBeforeSubmit = submitLabel.textContent;
        submitLabel.textContent = data.atcBusyLabel;
      }
      hideError();

      var sections = ['variant-added'];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } })
      );

      fetch(data.addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: items, sections: sections, sections_url: window.location.pathname })
      })
        .then(function (response) {
          /*
            Parsing must not throw before the status is known, otherwise an HTML error page would land in the catch below
            and be reported as "nothing was added" without that being established.
          */
          return response
            .json()
            .catch(function () {
              return {};
            })
            .then(function (json) {
              return { ok: response.ok, json: json };
            });
        })
        .then(function (result) {
          if (!result.ok) {
            /* /cart/add.js is atomic over `items`: a rejected request added nothing, so retrying is safe here only. */
            showError((result.json && (result.json.description || result.json.message)) || '');
            releaseSubmit();
            return;
          }
          return finishAfterAdd(result.json, items);
        })
        .catch(function (error) {
          /* Only reachable while the add was still in flight, meaning no response at all and an unchanged cart. */
          showError(error && error.message);
          releaseSubmit();
        });
    }

    /* Capture phase so this runs before Impact's product-form submit, which only knows a single line item. */
    root.addEventListener(
      'submit',
      function (event) {
        if (!form || event.target !== form) return;
        event.preventDefault();
        event.stopPropagation();
        submit();
      },
      true
    );

    if (form) {
      form.addEventListener('cart:error', function (event) {
        showError(event.detail && event.detail.error);
      });
    }

    /* Adopt whatever preset Liquid rendered as checked, so state and display agree from the very first paint. */
    var checkedPreset = null;
    el.presetInputs.forEach(function (input) {
      if (input.checked && !checkedPreset) checkedPreset = input.dataset.zhPresetKey;
    });
    if (checkedPreset) selectPreset(checkedPreset);

    /* ---- the step in view owns the preview ---- */

    /**
     * Hands the preview frame to whichever group step is crossing the middle of the screen, so scrolling alone is
     * enough to see what a step currently holds. rootMargin pinches the observer's viewport down to a band across the
     * centre; DOM order breaks the tie when two short steps share it, which keeps the answer independent of scroll
     * direction and of the order the entries happen to arrive in.
     *
     * Runs at every width. It used to be gated to 981px and up, because below that the media column scrolled away and
     * a scroll-driven handover would have hydrated pictures for a frame nobody could see. Since the stacked layout
     * pins the preview at the top of the screen, that reason is gone: on a phone the handover is the whole point,
     * the picture is the only thing on screen that reports what a step is about.
     */
    var stepObserver = null;

    function pickStepInView() {
      var next = null;
      el.groupSteps.forEach(function (step) {
        if (next || step.hidden || step.dataset.zhInView !== 'true') return;
        next = step.dataset.zhGroupStep;
      });
      if (next === state.mediaGroup) return;
      state.mediaGroup = next;
      syncMedia();
    }

    function observeSteps() {
      if (stepObserver || !el.steps.length) return;
      if (typeof window.IntersectionObserver !== 'function') return;

      stepObserver = new window.IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            entry.target.dataset.zhInView = entry.isIntersecting ? 'true' : 'false';
          });
          pickStepInView();
        },
        { rootMargin: '-45% 0px -45% 0px' }
      );
      /* Alle Schritte, nicht nur die Gruppen: das Zuruecktreten der Nachbarn braucht fuer jeden ein
         Urteil, die Bilduebergabe hoert weiterhin nur auf die Gruppenschritte. */
      el.steps.forEach(function (step) {
        stepObserver.observe(step);
      });

      /* Erst wenn beobachtet wird, darf gedimmt werden. Ohne JS bleibt jeder Schritt voll sichtbar,
         statt dass die halbe Seite blass steht und nichts sie je wieder aufhellt. */
      var config = root.querySelector('[data-zh-konf-config]');
      if (config) config.dataset.zhDim = 'true';
    }

    paintFloorPrices();
    render();
    observeSteps();
    track('konfigurator_view', { base: data.baseHandle });
  }

  function init() {
    Array.prototype.slice.call(document.querySelectorAll('[data-zh-konf-root]')).forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* The theme editor re-renders a section without a page load. */
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector ? event.target.querySelector('[data-zh-konf-root]') : null;
    if (root) initRoot(root);
  });
})();
